/* eslint-disable no-console */
/* eslint-disable react-hooks/exhaustive-deps */
import { fetchEventSource } from '@microsoft/fetch-event-source';
import Cookies from 'js-cookie';
import { useCallback , useEffect, useRef, useState } from 'react';
import showdown from 'showdown';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

import { agentChatApiClient , useLazyGetRunStatusQuery, useStopRunMutation } from '@/services/api/agentChatService';
import { agentChatSlice } from '@/services/slices/agentChat';
import { AgentChatStreamEvent,TAgentChatStreamEvent  } from '@/services/types/agent-chat-stream-types';
import { AgentChatAttachment, CreateAgentChatMessageRequest } from '@/services/types/agent-chat-types';
import { store } from '@/state/state';


const converter = new showdown.Converter({
  simpleLineBreaks: true,
  strikethrough: true,
  tables: true,
  tasklists: true,
  emoji: true,
});

export interface PreparedMessage {
  messageId: string;
  markdown: string;
  requestPayload: CreateAgentChatMessageRequest;
}

/**
 * Converts HTML input to Markdown and prepares the API request payload.
 */
export function prepareUserMessage(
  htmlContent: string,
  attachments: AgentChatAttachment[]
): PreparedMessage {
  const markdown = converter.makeMarkdown(htmlContent);
  const messageId = uuidv4();

  const requestPayload: CreateAgentChatMessageRequest = {
    message: {
      type: 'human',
      content: markdown,
      example: false,
      id: messageId,
      name: null,
    },
    attachment_ids: attachments.map((a) => a.id),
  };

  return { messageId, markdown, requestPayload };
}

// --- Types ---

export type StreamMode = 'start' | 'reconnect';

export interface StreamConfig {
  matterId: string;
  threadId: string;
  mode: StreamMode;
  signal: AbortSignal;
  // Optional params
  message?: CreateAgentChatMessageRequest;
  documentId?: string | null;
  exampleDemandLetterId?: string | null;
  webSearchEnabled?: boolean;
  model?: string;
}

export interface StreamCallbacks {
  onEvent: (event: TAgentChatStreamEvent) => void;
  onDisconnect: () => void;
  onError: (error: Error) => void;
}

// --- URL Builder (Internal) ---

function buildUrl(config: StreamConfig): string {
  const base = import.meta.env.VITE_APP_DJANGO_BACKEND_HOST;
  const prefix = `${base}/api/matters/${config.matterId}/agent-threads/${config.threadId}/messages`;

  if (config.mode === 'reconnect') {
    return `${prefix}/reconnect/`;
  }

  const params = new URLSearchParams();
  if (config.documentId) params.set('document_id', config.documentId);
  if (config.exampleDemandLetterId) params.set('example_demand_letter_id', config.exampleDemandLetterId);
  if (config.webSearchEnabled) params.set('web_search_enabled', 'true');
  if (config.model) params.set('model', config.model);

  const qs = params.toString();
  return `${prefix}/stream/${qs ? `?${qs}` : ''}`;
}

class FatalStreamError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'FatalStreamError';
    this.status = status;
  }
}

// --- Main Client ---

export async function createAgentStream(
  config: StreamConfig,
  callbacks: StreamCallbacks
): Promise<void> {
  const url = buildUrl(config);
  const body = config.mode === 'start' ? JSON.stringify(config.message) : undefined;

  await fetchEventSource(url, {
    method: 'POST',
    body,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      'X-CSRFToken': Cookies.get('csrftoken') ?? '',
    },
    openWhenHidden: true,
    signal: config.signal,

    async onopen(response) {
      if (response.ok) return; // Connection successful

      // Map HTTP errors to specific exceptions
      if (response.status === 401 || response.status === 403) {
        throw new FatalStreamError(`Authentication error: ${response.status}`, response.status);
      }
      if (response.status === 404) {
        throw new FatalStreamError(`Thread or Run not found: ${response.status}`, response.status);
      }
      if (response.status === 409) {
        throw new FatalStreamError('A response is still being generated. Please wait a moment and try again.', 409);
      }
      throw new FatalStreamError(`Stream connection failed: ${response.status}`, response.status);
    },

    onmessage(event) {
      try {
        const parsed = JSON.parse(event.data);
        const validatedEvent = AgentChatStreamEvent.parse(parsed);
        callbacks.onEvent(validatedEvent);
      } catch (error) {
        console.warn('[AgentStream] Parse error', error);
      }
    },

    onerror(error) {
      // fetchEventSource retries by default, but we generally want to control this manually
      // or treat it as a disconnect to trigger our custom reconnect logic.
      callbacks.onError(error instanceof Error ? error : new Error(String(error)));
      throw error; // Throwing here stops the internal fetchEventSource retry loop
    },

    onclose() {
      callbacks.onDisconnect();
    },
  });
}

interface DispatchOptions {
  threadId: string;
  matterId: string;
  onTerminalEvent: (restorePending: boolean) => void;
  onStreamActive: () => void;
}

export function useStreamEventDispatch({
  threadId,
  matterId,
  onTerminalEvent,
  onStreamActive,
}: DispatchOptions) {
  
  const refetchMessages = useCallback(async () => {
    await store.dispatch(
      agentChatApiClient.endpoints.listAgentChatMessages.initiate(
        { matterId, threadId },
        { forceRefetch: true }
      )
    ).unwrap();
  }, [matterId, threadId]);

  const handleEvent = useCallback((event: TAgentChatStreamEvent) => {
    switch (event.type) {
      case 'token':
        if (event.token.id) {
          store.dispatch(agentChatSlice.actions.handleAgentMessageChunk({
            threadId,
            message: event.token,
          }));
        }
        break;

      case 'message':
        if (event.message.id) {
          store.dispatch(agentChatSlice.actions.messageUpdated({
            threadId,
            messageId: event.message.id,
            message: event.message,
          }));
        }
        break;

      case 'control':
        handleControlEvent(event, onStreamActive, onTerminalEvent, refetchMessages);
        break;
      default:
        break;
    } 
  }, [threadId, onStreamActive, onTerminalEvent, refetchMessages]);

  return { handleEvent, refetchMessages };
}

// Helper to keep the switch statement clean
function handleControlEvent(
  event: Extract<TAgentChatStreamEvent, { type: 'control' }>,
  onActive: () => void,
  onTerminal: (restore: boolean) => void,
  onRefetch: () => void
) {
  switch (event.status) {
    case 'running':
      onActive();
      break;
    case 'completed':
      onRefetch();
      onTerminal(false); // Don't restore user input
      break;
    case 'failed':
      toast.error('Generation failed. Please try again.');
      onRefetch();
      onTerminal(true); // Restore user input
      break;
    case 'cancelled':
      onRefetch();
      onTerminal(true);
      break;
      default:
        break;
  }
}

// --- Types ---

export interface StreamOptions {
  documentId?: string | null;
  exampleDemandLetterId?: string | null;
  webSearchEnabled?: boolean;
  model?: string;
}

export interface PendingMessage {
  html: string;
  attachments: AgentChatAttachment[];
}

type StreamState = 'initializing' | 'idle' | 'sending' | 'streaming' | 'reconnecting';

// --- Constants ---

const RECONNECT_DELAY = 2000;
const RECONNECT_TIMEOUT = 60000;

// --- Hook ---

export function useAgentChatStream(matterId: string, threadId: string) {
  // 1. State
  const [streamState, setStreamState] = useState<StreamState>('initializing');
  const [restoredMessage, setRestoredMessage] = useState<PendingMessage | null>(null);
  
  // 2. Refs (Vital for breaking dependency cycles)
  const abortControllerRef = useRef<AbortController | null>(null);
  const pendingMessageRef = useRef<PendingMessage | null>(null);
  const mountedRef = useRef(true);
  
  // Ref for the reconnect function. This allows 'connect' to call it
  // without adding it to the dependency array (which causes loops).
  const attemptReconnectRef = useRef<() => void>(() => {});

  // 3. API Hooks
  const [getRunStatus] = useLazyGetRunStatusQuery();
  const [stopRunMutation] = useStopRunMutation();

  // 4. Lifecycle Methods

  const transitionToIdle = useCallback((shouldRestore: boolean) => {
    if (shouldRestore && pendingMessageRef.current) {
      setRestoredMessage(pendingMessageRef.current);
    }
    pendingMessageRef.current = null;
    setStreamState('idle');
  }, []);

  const handleStreamActive = useCallback(() => {
    setStreamState('streaming');
  }, []);

  const { handleEvent, refetchMessages } = useStreamEventDispatch({
    threadId,
    matterId,
    onStreamActive: handleStreamActive,
    onTerminalEvent: transitionToIdle,
  });

  // 5. Connection Logic

  /**
   * Connect to the stream.
   * Includes robust error handling to retry on network failures.
   */
  const connect = useCallback(async (config: Omit<StreamConfig, 'signal'>) => {
    // 1. Abort any existing connection
    abortControllerRef.current?.abort();
    const ac = new AbortController();
    abortControllerRef.current = ac;

    try {
      await createAgentStream(
        { ...config, signal: ac.signal },
        {
          onEvent: handleEvent,
          onDisconnect: () => {
             // If the server closes the connection cleanly (but not via a terminal event),
             // we should check status to be safe.
             if (mountedRef.current && !ac.signal.aborted) {
               attemptReconnectRef.current();
             }
          },
          onError: (err) => {
             // We just log here; the error will be re-thrown by the client 
             // and caught in the catch block below.
             console.warn('[Stream] Client reported error:', err); 
          }
        }
      );
    } catch (error) {
      // If the user effectively cancelled (unmount/nav), do nothing.
      if (ac.signal.aborted) return;

      const msg = error instanceof Error ? error.message : String(error);
      
      if (error instanceof FatalStreamError && error.status === 409) {
        if (config.message?.message.id) {
          store.dispatch(agentChatSlice.actions.messageDeleted(config.message.message.id));
        }
        transitionToIdle(true);
        toast.info('A response is still being generated', {
          description: 'Please wait for it to finish, then try again.',
        });
        return;
      }

      // When reconnecting, a 404 means the run completed between the
      // status check and the reconnect request – this is NOT a fatal
      // error.  Just fetch the final messages and go idle.
      if (config.mode === 'reconnect' && error instanceof FatalStreamError && error.status === 404) {
        console.log('[Stream] Run completed during reconnect, fetching messages');
        transitionToIdle(false);
        refetchMessages();
        return;
      }

      // Distinguish Fatal vs. Transient errors
      // Fatal: Auth errors (401/403) or Not Found (404). We should STOP.
      const isFatal = msg.includes('Authentication') || msg.includes('Thread or Run not found') || msg.includes('404') || msg.includes('Failed to fetch');

      if (isFatal) {
        toast.error('Unable to send message', {
          description: msg,
        });
        if (config.message?.message.id) {
          store.dispatch(agentChatSlice.actions.messageDeleted(config.message.message.id));
        }
        transitionToIdle(true); 
      } else {
        // Transient: Network errors, 500s, timeouts. We should RECONNECT.
        console.warn('Transient connection error (network?), attempting reconnect:', error);
        attemptReconnectRef.current();
      }
    }
  }, [handleEvent, transitionToIdle, refetchMessages]);

  /**
   * Reconnection Loop.
   * Polls the run status. If active -> reconnects SSE. If done -> fetches messages.
   */
  const attemptReconnection = useCallback(async () => {
    // Guard: Prevent double polling
    console.log('[Reconnection] Attempting reconnection');
    if (streamState === 'reconnecting') return;
    
    setStreamState('reconnecting');
    const startTime = Date.now();

    const poll = async () => {
      if (!mountedRef.current) return;

      console.log('[Reconnection] Polling for run status');

      // Timeout Check
      if (Date.now() - startTime > RECONNECT_TIMEOUT) {
        console.warn('[Reconnection] Timed out');
        transitionToIdle(true);
        refetchMessages();
        return;
      }

      try {
        const  data  = await getRunStatus({ matterId, threadId }).unwrap();

        console.log('[Reconnection] Run status:', data);
        
        // Check if unmounted during await
        if (!mountedRef.current) return;

        if (data?.has_active_run) {
          // Case A: Run is still active on backend -> Reconnect SSE
          console.log('[Reconnection] Run active, reconnecting stream...');
          setStreamState('streaming');
          await connect({ matterId, threadId, mode: 'reconnect' });
        } else {
          // Case B: Run is finished -> Stop polling, fetch latest messages
          console.log('[Reconnection] Run finished, fetching messages...');
          transitionToIdle(false);
          await refetchMessages();
        }
      } catch (err) {
        // Network error during poll? Wait and try again.
        console.warn('[Reconnection] Poll failed, retrying...', err);
        setTimeout(poll, RECONNECT_DELAY);
      }
    };
    
    poll();
  }, [matterId, threadId, streamState, getRunStatus, connect, transitionToIdle, refetchMessages]);

  // Keep the Ref updated with the latest version of the function
  useEffect(() => {
    attemptReconnectRef.current = attemptReconnection;
  }, [attemptReconnection]);


  // 6. Public API

  const sendMessage = useCallback(async (
    html: string, 
    attachments: AgentChatAttachment[], 
    opts?: StreamOptions
  ) => {
    const { messageId, requestPayload } = prepareUserMessage(html, attachments);
    
    pendingMessageRef.current = { html, attachments };
    setRestoredMessage(null);

    // Optimistic Update
    store.dispatch(agentChatSlice.actions.messageAdded({
      threadId,
      messageId,
      message: requestPayload.message,
      attachments,
    }));

    setStreamState('sending');

    // Start Stream
    try {
      await connect({
        mode: 'start',
        matterId,
        threadId,
        message: requestPayload,
        ...opts
      });
    } catch (err) {
       // This catch block handles IMMEDIATE failures (before the stream starts).
       // Once the stream starts, errors are handled in the connect() catch block.
       store.dispatch(agentChatSlice.actions.messageDeleted(messageId));
       transitionToIdle(true);
       toast.error('Failed to send message');
    }
  }, [matterId, threadId, connect, transitionToIdle]);

  const stopRun = useCallback(async () => {
    try {
      await stopRunMutation({ matterId, threadId }).unwrap();
      // We don't manually transition to idle here; we wait for the 
      // SSE 'cancelled' event or the polling logic to detect it.
    } catch {
      toast.error('Failed to stop run');
    }
  }, [matterId, threadId, stopRunMutation]);


  // 7. Initialization
  useEffect(() => {
    let active = true;
    setStreamState('initializing');
    
    abortControllerRef.current?.abort();

    const init = async () => {
      try {
        const { data } = await getRunStatus({ matterId, threadId });
        if (!active) return;

        if (data?.has_active_run) {
           setStreamState('streaming');
           await connect({ matterId, threadId, mode: 'reconnect' });
        } else {
           refetchMessages();
           setStreamState('idle');
        }
      } catch (e) {
        if(active) setStreamState('idle');
      }
    };

    init();

    return () => {
      active = false;
      mountedRef.current = false;
      abortControllerRef.current?.abort();
    };
  // Dependencies are now stable, so this only runs on ID changes
  }, [matterId, threadId, getRunStatus, connect, refetchMessages]);

  useEffect(() => {
    mountedRef.current = true;
  }, []);

  return {
    isInitializing: streamState === 'initializing',
    isSending: streamState === 'sending',
    isStreaming: streamState === 'streaming' || streamState === 'reconnecting',
    isReconnecting: streamState === 'reconnecting',
    restoredMessage,
    sendMessage,
    stopRun,
  };
}
